// ⚠️  GENERATED — do not edit. Run `npm run inferrex:generate:marketing` to regenerate.

/** Per-route schema.org graphs (Article / FAQPage), appended by buildHead(). */
export const PAGE_JSONLD: Readonly<Record<string, readonly object[]>> = {
  "/pricing": [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How does billing work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Monthly, quarterly (5% off), or annual (10% off) — chosen at signup and changeable at your next renewal. Every self-serve tier has a 30-day cancellation window."
          }
        },
        {
          "@type": "Question",
          "name": "Why no free trial?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Running AI inference, data sync, and self-healing on real APIs costs real compute. The entry tier gives you a genuine evaluation on real data — and you can cancel within 30 days."
          }
        },
        {
          "@type": "Question",
          "name": "What happens when I hit my tier's capacity?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Three options: add a capacity pack for a fixed monthly cost, enable overage to pay as you go, or upgrade your tier. All instant from your dashboard — no sales call."
          }
        },
        {
          "@type": "Question",
          "name": "What are AI operations?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "One meaningful AI action — like inferring an unknown API or AI-assisted healing. Connecting known providers, all sync operations, and automatic healing consume zero AI operations."
          }
        },
        {
          "@type": "Question",
          "name": "How does overage billing work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Opt-in per dimension. Usage beyond your tier and any packs is settled weekly. Charges below £9 accumulate until worth invoicing. Overage is capped at 3× your tier entitlement per month."
          }
        },
        {
          "@type": "Question",
          "name": "Do you charge per connector?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Every tier includes the full connector library — 8314+ connectors and growing."
          }
        },
        {
          "@type": "Question",
          "name": "Do you charge per user?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Unlimited users on every tier. Invite your entire team."
          }
        },
        {
          "@type": "Question",
          "name": "Can I switch tiers?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Upgrades are immediate. Downgrades take effect at your next billing cycle."
          }
        },
        {
          "@type": "Question",
          "name": "How is Enterprise / Sovereign priced?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Scoped per engagement. Contact us and we'll scope it together."
          }
        }
      ]
    }
  ],
  "/compare": [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is Inferrex an iPaaS?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "It sits underneath the category: integration is one layer of what comprehension produces, alongside the golden record, the data catalogue, compliance classification, and previewed migrations. The honest comparison is against the count of tools you run, not one incumbent."
          }
        },
        {
          "@type": "Question",
          "name": "How does migration onto Inferrex work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Point it at your systems. It infers each schema, classifies every field by meaning, and previews the full field mapping — with gap analysis — before a single record moves. There are no existing mappings to port because the mapping step is what goes away."
          }
        },
        {
          "@type": "Question",
          "name": "How does the pricing model differ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "MuleSoft licenses by capacity/cores, Boomi per connection, Workato per task. Inferrex is capacity-based with every feature included and unlimited users — no per-connector, per-connection, or per-execution meters. Details on the pricing page."
          }
        },
        {
          "@type": "Question",
          "name": "What about a system none of these platforms support?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "That's the point of comprehension: Inferrex infers structure from what the system exposes, so it isn't bounded by any connector catalogue — including the legacy systems usually written off as unmigratable."
          }
        },
        {
          "@type": "Question",
          "name": "Where are the incumbents genuinely stronger?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Mature connector catalogues, big partner ecosystems, and (MuleSoft especially) API-management suites. This page marks every partial displacement as partial — the trade-offs tab is part of the comparison."
          }
        }
      ]
    }
  ],
  "/mulesoft-alternative": [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How hard is it to migrate off MuleSoft?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Point Inferrex at the same source and destination systems and it infers each schema directly — there are no DataWeave mappings to port, because the mapping step itself goes away. Stack Intelligence previews every field mapping, with gap analysis, before a single record moves."
          }
        },
        {
          "@type": "Question",
          "name": "How does Inferrex pricing differ from MuleSoft's?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "MuleSoft licenses on a capacity/core basis, typically alongside consultant-led delivery. Inferrex is capacity-based with every feature included and unlimited users — no per-connector or per-seat line items. Tiers are on the pricing page."
          }
        },
        {
          "@type": "Question",
          "name": "Does Inferrex cover the connectors MuleSoft has?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Inferrex doesn't work from a connector catalogue at all — it infers a system's structure from what the system exposes, so coverage isn't bounded by what's been pre-built. Systems the platform has already comprehended are browsable in The Corpus."
          }
        },
        {
          "@type": "Question",
          "name": "What does MuleSoft do that Inferrex doesn't?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "MuleSoft's API-management suite and its certified-consultant ecosystem are genuinely strong, and Inferrex doesn't claim either. If your need is API gateway governance rather than data integration, that's a real MuleSoft strength."
          }
        },
        {
          "@type": "Question",
          "name": "What happens when a provider changes its API?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "On MuleSoft the mapping breaks and an engineer fixes it. Inferrex watches provider docs, specs and changelogs, re-infers the changed structure, and repairs the mapping — escalating only when judgement is needed."
          }
        }
      ]
    }
  ],
  "/boomi-alternative": [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How hard is it to migrate off Boomi?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "There are no Boomi processes to port — Inferrex infers each connected system's schema directly and classifies every field by meaning. Stack Intelligence previews the full field mapping, with gap analysis, before anything moves."
          }
        },
        {
          "@type": "Question",
          "name": "How does Inferrex pricing differ from Boomi's?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Boomi prices per connection, so the bill grows with your integration footprint. Inferrex is capacity-based with every feature included and unlimited users — connecting more systems doesn't add line items. Tiers are on the pricing page."
          }
        },
        {
          "@type": "Question",
          "name": "Does Inferrex cover the connectors Boomi has?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Coverage works differently: Inferrex infers structure from the live system rather than shipping a pre-built connector per product, so it isn't bounded by a catalogue. What the platform has already comprehended is browsable in The Corpus."
          }
        },
        {
          "@type": "Question",
          "name": "What does Boomi do that Inferrex doesn't?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Boomi is broad, capable and established — years of templates, a large partner network, and a proven on-prem runtime in the Atom. For a stack of entirely mainstream, stable APIs, that maturity is real."
          }
        },
        {
          "@type": "Question",
          "name": "What happens when a provider changes its API?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "On Boomi the mapping breaks and an engineer fixes it. Inferrex re-infers the changed structure and repairs the mapping itself — change monitoring sees the shift at the source, usually before it would have surfaced as a failed sync."
          }
        }
      ]
    }
  ],
  "/workato-alternative": [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How hard is it to migrate off Workato?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Recipes don't port — they don't need to. Inferrex infers each system's schema and classifies every field by meaning, so the integrations recipes were hand-wiring arrive as consequences of comprehension. Migration previews show every field mapping before anything moves."
          }
        },
        {
          "@type": "Question",
          "name": "How does Inferrex pricing differ from Workato's?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Workato bills by task/recipe consumption, so cost scales with how often your automations run. Inferrex is capacity-based with every feature included and unlimited users — there is no per-execution meter. Tiers are on the pricing page."
          }
        },
        {
          "@type": "Question",
          "name": "Does Inferrex cover the connectors Workato has?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Inferrex isn't bounded by a connector catalogue — it infers structure from what a system exposes, including systems no one ever built a connector for. What's already comprehended is browsable in The Corpus."
          }
        },
        {
          "@type": "Question",
          "name": "What does Workato do that Inferrex doesn't?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Business-user workflow automation is Workato's genuine strength — an accessible recipe model and a deep template library. If your need is primarily human-workflow glue between mainstream SaaS tools, that fit is real."
          }
        },
        {
          "@type": "Question",
          "name": "What happens when a provider changes its API?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "On Workato the recipe's mapping breaks and someone rebuilds it. Inferrex re-infers the changed structure and heals the mapping — the maintenance loop the automation was supposed to remove actually goes away."
          }
        }
      ]
    }
  ],
  "/manifesto": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Manifesto",
      "description": "How Inferrex operates — the operating doctrine behind the product. One version, public and internal: radical transparency, trust first, integrity over growth.",
      "url": "https://inferrex.com/manifesto",
      "mainEntityOfPage": "https://inferrex.com/manifesto",
      "image": "https://inferrex.com/og/manifesto.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      }
    }
  ],
  "/insights/the-state-cannot-see-itself": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "The State Cannot See Itself",
      "description": "British government does not have a technology problem; it has a comprehension problem — why state IT keeps failing at the data layer.",
      "url": "https://inferrex.com/insights/the-state-cannot-see-itself",
      "mainEntityOfPage": "https://inferrex.com/insights/the-state-cannot-see-itself",
      "image": "https://inferrex.com/og/insights-the-state-cannot-see-itself.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/comprehension-over-connection": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Comprehension Over Connection",
      "description": "The integration industry spent twenty years getting better at connecting systems. That was the wrong problem — comprehension is the right one.",
      "url": "https://inferrex.com/insights/comprehension-over-connection",
      "mainEntityOfPage": "https://inferrex.com/insights/comprehension-over-connection",
      "image": "https://inferrex.com/og/insights-comprehension-over-connection.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/the-nxn-problem": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "The N×N Problem",
      "description": "Why every connector-catalogue platform collapses under its own count — and how a canonical layer turns N×N integrations into N mappings.",
      "url": "https://inferrex.com/insights/the-nxn-problem",
      "mainEntityOfPage": "https://inferrex.com/insights/the-nxn-problem",
      "image": "https://inferrex.com/og/insights-the-nxn-problem.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/lingua-franca-for-business-data": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "A Lingua Franca for Business Data",
      "description": "A lingua franca is not a translator between two systems; it is a shared language above all of them. What that means for business data.",
      "url": "https://inferrex.com/insights/lingua-franca-for-business-data",
      "mainEntityOfPage": "https://inferrex.com/insights/lingua-franca-for-business-data",
      "image": "https://inferrex.com/og/insights-lingua-franca-for-business-data.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/everyone-can-be-different": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Why Everyone Can Be Different",
      "description": "Most integration platforms demand conformity to one schema — and conformity is exactly what breaks. Comprehension lets every system stay itself.",
      "url": "https://inferrex.com/insights/everyone-can-be-different",
      "mainEntityOfPage": "https://inferrex.com/insights/everyone-can-be-different",
      "image": "https://inferrex.com/og/insights-everyone-can-be-different.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/self-healing-integration": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "What Self-Healing Integration Actually Means",
      "description": "Retries, alerts and auto-reconnect are not self-healing. Real self-healing detects that an API changed and repairs the mapping itself.",
      "url": "https://inferrex.com/insights/self-healing-integration",
      "mainEntityOfPage": "https://inferrex.com/insights/self-healing-integration",
      "image": "https://inferrex.com/og/insights-self-healing-integration.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/hidden-cost-brittle-integrations": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "The Hidden Cost of Brittle Integrations",
      "description": "The visible cost of a broken integration is the smaller half. The larger half is silent bad data, stalled roadmaps and engineers on patrol.",
      "url": "https://inferrex.com/insights/hidden-cost-brittle-integrations",
      "mainEntityOfPage": "https://inferrex.com/insights/hidden-cost-brittle-integrations",
      "image": "https://inferrex.com/og/insights-hidden-cost-brittle-integrations.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/inferring-api-structure": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Inferring API Structure — How Schema Inference Works | Inferrex",
      "description": "The interesting capability is not connecting a known API; it is reading one nobody built a connector for. How schema inference works.",
      "url": "https://inferrex.com/insights/inferring-api-structure",
      "mainEntityOfPage": "https://inferrex.com/insights/inferring-api-structure",
      "image": "https://inferrex.com/og/insights-inferring-api-structure.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/sovereign-integration-infrastructure": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Sovereign Integration Infrastructure",
      "description": "Why integration infrastructure with zero external dependencies — your hardware, your bundled models, your perimeter — is now a real requirement.",
      "url": "https://inferrex.com/insights/sovereign-integration-infrastructure",
      "mainEntityOfPage": "https://inferrex.com/insights/sovereign-integration-infrastructure",
      "image": "https://inferrex.com/og/insights-sovereign-integration-infrastructure.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/air-gapped-not-crippled": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Air-Gapped Doesn’t Mean Crippled",
      "description": "Air-gapped editions are usually crippled because most AI is a call to an external model. Own the models and the perimeter never has to open.",
      "url": "https://inferrex.com/insights/air-gapped-not-crippled",
      "mainEntityOfPage": "https://inferrex.com/insights/air-gapped-not-crippled",
      "image": "https://inferrex.com/og/insights-air-gapped-not-crippled.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/saas-to-critical-infrastructure": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "From AI SaaS to Critical National Infrastructure",
      "description": "A class of software is crossing from application you can swap to infrastructure a country depends on. What that shift demands of vendors.",
      "url": "https://inferrex.com/insights/saas-to-critical-infrastructure",
      "mainEntityOfPage": "https://inferrex.com/insights/saas-to-critical-infrastructure",
      "image": "https://inferrex.com/og/insights-saas-to-critical-infrastructure.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/data-sovereignty-regulated-industries": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Data Sovereignty for Regulated Industries",
      "description": "What data sovereignty actually means, the five questions a regulated buyer must ask, and how deployment mode answers each one.",
      "url": "https://inferrex.com/insights/data-sovereignty-regulated-industries",
      "mainEntityOfPage": "https://inferrex.com/insights/data-sovereignty-regulated-industries",
      "image": "https://inferrex.com/og/insights-data-sovereignty-regulated-industries.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is data residency the same as data sovereignty?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Residency is about where data is stored; sovereignty is the broader principle of which jurisdiction's laws govern your data across its entire path — storage, processing, transit, and the legal reach over whoever controls the infrastructure. You can satisfy residency and still have a sovereignty exposure if the data is processed, or the infrastructure controlled, in the wrong jurisdiction."
          }
        },
        {
          "@type": "Question",
          "name": "Does UK GDPR require my data to stay in the UK?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Not exactly. UK GDPR governs how personal data is processed and restricts international transfers to jurisdictions without adequate protection, using mechanisms like adequacy decisions and Standard Contractual Clauses. It doesn't mandate UK-only storage, but it does require you to control and document where personal data goes and under what safeguards — which is why the egress and processing-chain questions matter. Confirm specifics with your own counsel."
          }
        },
        {
          "@type": "Question",
          "name": "What about financial data residency rules?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Financial regulators increasingly expect demonstrable control over data location and operational resilience — that critical functions keep working without dependencies you don't control. The specifics vary by regulator and jurisdiction, but the common thread is that \"we think it's probably fine\" is no longer an acceptable answer; you need enforced, auditable control. Treat this as a question for your compliance team against your specific regulatory regime."
          }
        },
        {
          "@type": "Question",
          "name": "Is the integration layer really a sovereignty risk?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, and it's the one most often overlooked. Anything that moves data is a processor, and integration middleware frequently has a control plane that phones home and an \"AI\" feature that calls an external model — both of which egress data to jurisdictions you may not have chosen. Put your integration layer through the same residency, egress, and processing-chain scrutiny you apply to your database."
          }
        },
        {
          "@type": "Question",
          "name": "What's the difference between a policy and an enforced control?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A policy is a vendor promising to behave a certain way; an enforced control is a technical measure that makes the unwanted behaviour impossible, and that you (or your auditor) can verify. Regulators increasingly favour the second. \"We won't send your data outside the region\" is a policy; \"this deployment makes no external calls, enforced at the network and application layer, and here's the validation\" is a control."
          }
        },
        {
          "@type": "Question",
          "name": "Does choosing a sovereign or air-gapped deployment mean losing capability?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "It shouldn't, though it often does with platforms whose intelligence is rented from an external model provider — cut off, they degrade. A platform whose models are owned and run locally keeps full capability when deployed sovereign or air-gapped. The capability loss isn't inherent to isolation; it's a symptom of rented intelligence."
          }
        }
      ]
    }
  ],
  "/insights/one-person-production-infrastructure": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "How One Person Built Production-Grade Infrastructure",
      "description": "A self-taught salesperson built a seventeen-service production platform solo — with agents, manifests and codegen, not by prompting a chatbot.",
      "url": "https://inferrex.com/insights/one-person-production-infrastructure",
      "mainEntityOfPage": "https://inferrex.com/insights/one-person-production-infrastructure",
      "image": "https://inferrex.com/og/insights-one-person-production-infrastructure.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/self-sovereign-ai-training": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Building a Self-Sovereign AI Training Loop",
      "description": "Why we improve our models without distilling from frontier providers: a closed training loop fed only by our own systems and corrections.",
      "url": "https://inferrex.com/insights/self-sovereign-ai-training",
      "mainEntityOfPage": "https://inferrex.com/insights/self-sovereign-ai-training",
      "image": "https://inferrex.com/og/insights-self-sovereign-ai-training.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/own-ai-layer": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Why We Built Our Own AI Layer",
      "description": "Wiring in a frontier API hands you three dependencies you do not control and one wall you can never move. Why Inferrex built its own AI layer.",
      "url": "https://inferrex.com/insights/own-ai-layer",
      "mainEntityOfPage": "https://inferrex.com/insights/own-ai-layer",
      "image": "https://inferrex.com/og/insights-own-ai-layer.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/embedded-integration-infrastructure": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Embedded Integration Infrastructure",
      "description": "The Stripe-for-data-integration model: embed one integration-infrastructure partner and every tool in your product speaks every other tool.",
      "url": "https://inferrex.com/insights/embedded-integration-infrastructure",
      "mainEntityOfPage": "https://inferrex.com/insights/embedded-integration-infrastructure",
      "image": "https://inferrex.com/og/insights-embedded-integration-infrastructure.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/inferrex-vs-ipaas": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Inferrex vs Traditional iPaaS",
      "description": "An honest, capability-for-capability comparison with traditional iPaaS — including where the incumbents are genuinely stronger.",
      "url": "https://inferrex.com/insights/inferrex-vs-ipaas",
      "mainEntityOfPage": "https://inferrex.com/insights/inferrex-vs-ipaas",
      "image": "https://inferrex.com/og/insights-inferrex-vs-ipaas.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ],
  "/insights/api-schema-corpus": [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "What’s Inside an API Schema Corpus",
      "description": "A big corpus number means nothing on its own. What matters is depth, normalisation into one canonical model, and per-field classification.",
      "url": "https://inferrex.com/insights/api-schema-corpus",
      "mainEntityOfPage": "https://inferrex.com/insights/api-schema-corpus",
      "image": "https://inferrex.com/og/insights-api-schema-corpus.png",
      "author": {
        "@type": "Person",
        "name": "Aaron Gammon"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Inferrex",
        "logo": {
          "@type": "ImageObject",
          "url": "https://inferrex.com/logos/inferrex-icon-512.png"
        }
      },
      "datePublished": "2026-06"
    }
  ]
};
